import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, Building2, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getJobs, getCompanies } from '../lib/api';
import { EmptyState, LoadingScreen, PageHero, TealButton, staggerContainer, staggerItem } from '../components/common/BrandElements';

const CareersPage = () => {
  const { t, isRTL } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [jobsRes, companiesRes] = await Promise.all([getJobs(), getCompanies()]);
        setJobs(jobsRes.data.filter(j => j.is_active));
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getCompany = (companyId) => companies.find(c => c.id === companyId);

  const jobTypeLabels = {
    full_time: { ar: 'دوام كامل', en: 'Full Time' },
    part_time: { ar: 'دوام جزئي', en: 'Part Time' },
    contract: { ar: 'عقد', en: 'Contract' }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="careers-page">
      <PageHero
        kicker={t('انضم إلينا', 'Join Us')}
        title={t('فرص العمل', 'Career Opportunities')}
        description={t('انضم إلى فريق يعمل ضمن بيئة مستقرة ومتطلعة إلى المستقبل.', 'Join a team working in a stable, future-facing environment.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {jobs.length === 0 ? (
            <EmptyState
              title={t('لا توجد فرص عمل متاحة حالياً', 'No job openings available at the moment')}
              description={t('يمكنك التواصل معنا لمعرفة المزيد.', 'Contact us to learn more.')}
              action={<TealButton to="/contact">{t('تواصل معنا', 'Contact Us')}</TealButton>}
            />
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="mx-auto max-w-5xl space-y-6">
              {jobs.map((job) => {
                const company = getCompany(job.company_id);
                return (
                  <motion.article key={job.id} variants={staggerItem} className="rounded-[2rem] border border-raad-teal/15 bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:border-raad-teal/40 hover:shadow-teal md:p-8">
                    <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-start">
                      <div>
                        <h2 className="text-2xl font-black text-raad-charcoal">
                          {t(job.title_ar, job.title_en)}
                        </h2>
                        <div className="mt-4 flex flex-wrap gap-3 text-sm font-bold text-raad-graphite/65">
                          {company && (
                            <Link to={`/companies/${company.slug}`} className="inline-flex items-center gap-2 rounded-full bg-raad-mist px-3 py-1 hover:text-raad-teal">
                              <Building2 className="h-4 w-4 text-raad-teal" />
                              {t(company.name_ar, company.name_en)}
                            </Link>
                          )}
                          {(job.location_ar || job.location_en) && (
                            <span className="inline-flex items-center gap-2 rounded-full bg-raad-mist px-3 py-1">
                              <MapPin className="h-4 w-4 text-raad-teal" />
                              {t(job.location_ar, job.location_en)}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-2 rounded-full bg-raad-mist px-3 py-1">
                            <Briefcase className="h-4 w-4 text-raad-teal" />
                            {t(jobTypeLabels[job.job_type]?.ar, jobTypeLabels[job.job_type]?.en)}
                          </span>
                          {job.closing_date && (
                            <span className="inline-flex items-center gap-2 rounded-full bg-raad-mist px-3 py-1">
                              <Calendar className="h-4 w-4 text-raad-teal" />
                              {t('ينتهي', 'Closes')}: {new Date(job.closing_date).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button asChild className="rounded-full bg-raad-charcoal px-7 text-white hover:bg-raad-teal">
                        <Link to={`/careers/${job.id}/apply`}>{t('تقدم الآن', 'Apply Now')}</Link>
                      </Button>
                    </div>

                    <p className="mt-5 text-sm leading-7 text-raad-graphite/65 whitespace-pre-wrap line-clamp-4">
                      {t(job.description_ar, job.description_en)}
                    </p>
                  </motion.article>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
